/**
 * Universal profile theme catalog.
 * Niche origin is browsing metadata only — not a restriction.
 * audience: "creator" | "fan" | "both" — enforced server-side on unlock/apply.
 */

import { hasCreatorAccess } from "../utils/roleUtils";

export const PROFILE_THEME_FILTERS = [
  { id: "forYou", label: "For You" },
  { id: "editorial", label: "Editorial" },
  { id: "workshop", label: "Workshop" },
  { id: "wellness", label: "Wellness" },
  { id: "arts", label: "Arts" },
  { id: "beauty", label: "Beauty" },
  { id: "food", label: "Food" },
  { id: "fitness", label: "Fitness" },
  { id: "auto", label: "Auto & Trades" },
  { id: "mind", label: "Mind & Growth" },
  { id: "independent", label: "Independent" },
];

export const PROFILE_THEME_ORIGIN_LABELS = Object.fromEntries(
  PROFILE_THEME_FILTERS.map((f) => [f.id, f.label])
);

export const DEFAULT_PROFILE_THEME_ID = "goldenHour";

/** Retired theme ids → current catalog replacement */
export const PROFILE_THEME_MIGRATIONS = {
  studioRed: "goldenHour",
  velvetStage: "roseQuartz",
  midnightSession: "citrusPop",
  rawConcrete: "paperInk",
};

function baseVars(overrides = {}) {
  return {
    "--pt-bg": "#F2EFE9",
    "--pt-bg-image": "none",
    "--pt-surface": "#FFFFFF",
    "--pt-surface-2": "#EDE9E2",
    "--pt-border": "#DDD8CE",
    "--pt-border-strong": "#C8C0B2",
    "--pt-text": "#0E0C0A",
    "--pt-text-mid": "#3C3830",
    "--pt-text-muted": "#9A9088",
    "--pt-accent": "#C4790A",
    "--pt-accent-soft": "#FDF3E0",
    "--pt-btn-bg": "#C4790A",
    "--pt-btn-color": "#FFFFFF",
    "--pt-btn-border": "#C4790A",
    "--pt-btn-radius": "10px",
    "--pt-card-bg": "#FFFFFF",
    "--pt-card-border": "#DDD8CE",
    "--pt-card-border-width": "1px",
    "--pt-card-radius": "14px",
    "--pt-media-border": "transparent",
    "--pt-media-border-width": "0px",
    "--pt-avatar-radius": "50%",
    "--pt-avatar-border": "#0E0C0A",
    "--pt-avatar-border-width": "2px",
    "--pt-name-font": '"Cormorant Garamond", Georgia, serif',
    "--pt-name-weight": "500",
    "--pt-name-tracking": "0",
    "--pt-meta-transform": "none",
    "--pt-meta-tracking": "0",
    "--pt-tier-top": "transparent",
    "--pt-tier-top-width": "0px",
    // Bridge into existing .cp-page tokens
    "--bg": "var(--pt-bg)",
    "--surface": "var(--pt-surface)",
    "--surface-2": "var(--pt-surface-2)",
    "--border": "var(--pt-border)",
    "--border-strong": "var(--pt-border-strong)",
    "--text": "var(--pt-text)",
    "--text-mid": "var(--pt-text-mid)",
    "--text-muted": "var(--pt-text-muted)",
    "--gold": "var(--pt-accent)",
    "--gold-light": "var(--pt-accent-soft)",
    "--niche": "var(--pt-accent)",
    "--niche-light": "var(--pt-accent-soft)",
    "--niche-mid": "var(--pt-text-mid)",
    "--niche-glow": "color-mix(in srgb, var(--pt-accent) 25%, transparent)",
    ...overrides,
  };
}

function firstHex(str) {
  const m = String(str || "").match(/#([0-9A-Fa-f]{6})/);
  return m ? m[0] : null;
}

function lastHex(str) {
  const m = String(str || "").match(/#([0-9A-Fa-f]{6})/g);
  return m ? m[m.length - 1] : null;
}

function hexLum(hex) {
  const n = parseInt(hex.slice(1), 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}

function mixPaper(hex) {
  const n = parseInt(hex.slice(1), 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  const t = 0.82;
  const lr = Math.round(r + (251 - r) * t);
  const lg = Math.round(g + (247 - g) * t);
  const lb = Math.round(b + (238 - b) * t);
  return `#${[lr, lg, lb].map((x) => x.toString(16).padStart(2, "0")).join("")}`;
}

function previewFromDef(def) {
  if (def.bg) {
    return {
      background: def.bg,
      backgroundImage: def.swatch,
      backgroundSize: def.swatchSize || "auto",
      backgroundPosition: def.swatchPos || "0 0",
    };
  }
  if (def.swatchSize) {
    return {
      backgroundImage: def.swatch,
      backgroundSize: def.swatchSize,
      ...(def.swatchPos ? { backgroundPosition: def.swatchPos } : {}),
    };
  }
  return { background: def.swatch };
}

function extraThemeFromDef(def) {
  const preview = previewFromDef(def);
  const swatchHex = firstHex(def.bg) || firstHex(def.swatch) || "#F2EFE9";
  const accent = def.accent || lastHex(def.swatch) || swatchHex;
  const bgSolid = hexLum(swatchHex) < 0.62 ? mixPaper(swatchHex) : swatchHex;
  const bgImage = preview.backgroundImage
    || (String(preview.background || "").includes("gradient") ? preview.background : "none");
  return {
    id: def.id,
    audience: def.audience,
    name: def.name,
    origin: def.origin,
    originLabel: PROFILE_THEME_ORIGIN_LABELS[def.origin] || def.origin,
    unlockType: def.unlockType,
    ...(def.creditsCost != null ? { creditsCost: def.creditsCost } : {}),
    ...(def.achievement ? { achievement: def.achievement } : {}),
    desc: def.desc,
    isNew: def.isNew !== false,
    preview,
    hero: preview,
    variables: baseVars({
      "--pt-bg": bgSolid,
      "--pt-bg-image": bgImage,
      ...(preview.backgroundSize ? { "--pt-bg-size": preview.backgroundSize } : {}),
      ...(preview.backgroundPosition ? { "--pt-bg-position": preview.backgroundPosition } : {}),
      "--pt-accent": accent,
      "--pt-accent-soft": mixPaper(accent),
      "--pt-btn-bg": accent,
      "--pt-btn-border": accent,
      "--pt-avatar-border": accent,
    }),
  };
}

const CORE_PROFILE_THEMES = {
  /* ── Editorial ── */
  paperInk: {
    id: "paperInk",
    audience: "creator",
    name: "Paper & Ink",
    desc: "Fine rule lines and quiet restraint.",
    isNew: false,
    hero: { background: "linear-gradient(135deg,#FAFAF8,#EDEAE0)" },
    origin: "editorial",
    originLabel: "✦ Editorial",
    unlockType: "credits",
    creditsCost: 1800,
    preview: {
      background: "#FAFAF8",
      backgroundImage:
        "repeating-linear-gradient(90deg,rgba(14,12,10,0.045) 0px,rgba(14,12,10,0.045) 1px,transparent 1px,transparent 22px)",
    },
    variables: baseVars({
      "--pt-bg": "#FAFAF8",
      "--pt-bg-image":
        "repeating-linear-gradient(90deg, rgba(14,12,10,0.045) 0px, rgba(14,12,10,0.045) 1px, transparent 1px, transparent 28px)",
      "--pt-surface": "#FFFFFF",
      "--pt-border": "#DDD8CE",
      "--pt-text": "#0E0C0A",
      "--pt-text-mid": "#4A453E",
      "--pt-text-muted": "#8A8278",
      "--pt-accent": "#0E0C0A",
      "--pt-accent-soft": "#F5F2EC",
      "--pt-btn-bg": "#FFFFFF",
      "--pt-btn-color": "#0E0C0A",
      "--pt-btn-border": "#0E0C0A",
      "--pt-card-border": "#DDD8CE",
      "--pt-avatar-border": "#0E0C0A",
      "--pt-name-font": '"Cormorant Garamond", Georgia, serif',
      "--pt-name-weight": "500",
    }),
  },
  galleryWall: {
    id: "galleryWall",
    audience: "creator",
    name: "Gallery Wall",
    desc: "A bold frame and a caption line.",
    isNew: false,
    hero: { background: "#F3F1EA" },
    origin: "editorial",
    originLabel: "✦ Editorial",
    unlockType: "achievement",
    achievement: { achievementType: "subscriberCount", value: 500 },
    preview: { background: "#F7F6F2" },
    variables: baseVars({
      "--pt-bg": "#F7F6F2",
      "--pt-bg-image": "none",
      "--pt-text": "#0E0C0A",
      "--pt-text-muted": "#9A9088",
      "--pt-accent": "#0E0C0A",
      "--pt-accent-soft": "#F0EEE8",
      "--pt-btn-bg": "#0E0C0A",
      "--pt-btn-color": "#FFFFFF",
      "--pt-btn-border": "#0E0C0A",
      "--pt-btn-radius": "4px",
      "--pt-card-border": "#0E0C0A",
      "--pt-card-border-width": "2.5px",
      "--pt-card-radius": "4px",
      "--pt-media-border": "#0E0C0A",
      "--pt-media-border-width": "2.5px",
      "--pt-avatar-radius": "0",
      "--pt-avatar-border": "#0E0C0A",
      "--pt-avatar-border-width": "3px",
      "--pt-meta-transform": "uppercase",
      "--pt-meta-tracking": "0.04em",
    }),
  },
  studioGrid: {
    id: "studioGrid",
    audience: "creator",
    name: "Studio Grid",
    desc: "Precise, architectural, exacting.",
    isNew: false,
    hero: { background: "#F5F4EE" },
    origin: "editorial",
    originLabel: "✦ Editorial",
    unlockType: "credits",
    creditsCost: 2200,
    preview: {
      background: "#F8F8F6",
      backgroundImage:
        "linear-gradient(rgba(14,12,10,0.06) 1px,transparent 1px),linear-gradient(90deg,rgba(14,12,10,0.06) 1px,transparent 1px)",
      backgroundSize: "16px 16px",
    },
    variables: baseVars({
      "--pt-bg": "#F8F8F6",
      "--pt-bg-image":
        "linear-gradient(rgba(14,12,10,0.055) 1px, transparent 1px), linear-gradient(90deg, rgba(14,12,10,0.055) 1px, transparent 1px)",
      "--pt-bg-size": "20px 20px",
      "--pt-text-muted": "#8A8278",
      "--pt-accent": "#0E0C0A",
      "--pt-btn-bg": "#0E0C0A",
      "--pt-btn-color": "#FFFFFF",
      "--pt-btn-border": "#0E0C0A",
      "--pt-card-border": "#D5D2CA",
      "--pt-avatar-border": "#0E0C0A",
    }),
  },
  linenPress: {
    id: "linenPress",
    audience: "creator",
    name: "Linen Press",
    desc: "Woven texture, a wax-seal detail.",
    isNew: false,
    hero: { background: "#F0E9D6" },
    origin: "editorial",
    originLabel: "✦ Editorial",
    unlockType: "achievement",
    achievement: { achievementType: "monthsActive", value: 3 },
    preview: {
      background: "#F5F0E6",
      backgroundImage:
        "repeating-linear-gradient(45deg,rgba(14,12,10,0.025) 0px,rgba(14,12,10,0.025) 1px,transparent 1px,transparent 5px)",
    },
    variables: baseVars({
      "--pt-bg": "#F5F0E6",
      "--pt-bg-image":
        "repeating-linear-gradient(45deg, rgba(14,12,10,0.025) 0px, rgba(14,12,10,0.025) 1px, transparent 1px, transparent 5px)",
      "--pt-surface": "#FFFDF8",
      "--pt-border": "#E8DCC0",
      "--pt-text": "#2A2216",
      "--pt-text-mid": "#5A4E3C",
      "--pt-text-muted": "#9A8A70",
      "--pt-accent": "#C4790A",
      "--pt-accent-soft": "#FDF3E0",
      "--pt-btn-bg": "#FFFFFF",
      "--pt-btn-color": "#C4790A",
      "--pt-btn-border": "#C4790A",
      "--pt-card-bg": "#FFFDF8",
      "--pt-card-border": "#E8DCC0",
      "--pt-avatar-border": "#C4790A",
      "--pt-avatar-border-width": "2px",
    }),
  },

  /* ── Wellness ── */
  sageMorning: {
    id: "sageMorning",
    audience: "creator",
    name: "Sage Morning",
    desc: "Soft green and quiet mornings.",
    isNew: false,
    hero: { background: "#E0EBE3" },
    origin: "wellness",
    originLabel: "🌿 Wellness",
    unlockType: "free",
    preview: { background: "#E8F0EA" },
    variables: baseVars({
      "--pt-bg": "#E8F0EA",
      "--pt-accent": "#5C8B6E",
      "--pt-accent-soft": "#EAF2ED",
      "--pt-btn-bg": "#5C8B6E",
      "--pt-btn-border": "#5C8B6E",
      "--pt-text-muted": "#6E8578",
      "--pt-avatar-border": "#5C8B6E",
    }),
  },
  warmEarth: {
    id: "warmEarth",
    audience: "creator",
    name: "Warm Earth",
    desc: "Deep, grounded, outdoors.",
    isNew: false,
    hero: { background: "#8B7355" },
    origin: "wellness",
    originLabel: "🌿 Wellness",
    unlockType: "credits",
    creditsCost: 1800,
    preview: { background: "#8B7355" },
    variables: baseVars({
      "--pt-bg": "#F3EDE4",
      "--pt-accent": "#8B7355",
      "--pt-accent-soft": "#F5EDE2",
      "--pt-btn-bg": "#8B7355",
      "--pt-btn-border": "#8B7355",
      "--pt-text": "#2A2216",
      "--pt-avatar-border": "#C4860A",
    }),
  },

  /* ── Arts (rebalanced) ── */
  goldenHour: {
    id: "goldenHour",
    audience: "creator",
    name: "Golden Hour",
    desc: "That last warm light before sundown.",
    isNew: false,
    hero: { background: "linear-gradient(160deg,#FBEED0,#F2D294)" },
    origin: "arts",
    originLabel: "🎨 Arts",
    unlockType: "free",
    preview: {
      background: "linear-gradient(160deg,#FDF3E0,#F5DCA8)",
    },
    variables: baseVars({
      "--pt-bg": "#FDF6E8",
      "--pt-bg-image": "linear-gradient(160deg, #FDF3E0 0%, #F5DCA8 100%)",
      "--pt-accent": "#C4790A",
      "--pt-accent-soft": "#FDF3E0",
      "--pt-btn-bg": "#C4790A",
      "--pt-btn-border": "#C4790A",
      "--pt-text": "#2A2216",
      "--pt-avatar-border": "#C4790A",
    }),
  },
  citrusPop: {
    id: "citrusPop",
    audience: "creator",
    name: "Citrus Pop",
    desc: "Bright, lively, a little loud.",
    isNew: false,
    hero: { background: "linear-gradient(160deg,#FADCC8,#EE9C6A)" },
    origin: "arts",
    originLabel: "🎨 Arts",
    unlockType: "credits",
    creditsCost: 2000,
    preview: {
      background: "linear-gradient(160deg,#FCE4D8,#F0A87E)",
    },
    variables: baseVars({
      "--pt-bg": "#FDF0E8",
      "--pt-bg-image": "linear-gradient(160deg, #FCE4D8 0%, #F0A87E 100%)",
      "--pt-accent": "#E07A3A",
      "--pt-accent-soft": "#FCE4D8",
      "--pt-btn-bg": "#E07A3A",
      "--pt-btn-border": "#E07A3A",
      "--pt-text": "#2A1A10",
      "--pt-avatar-border": "#E07A3A",
    }),
  },
  roseQuartz: {
    id: "roseQuartz",
    audience: "creator",
    name: "Rose Quartz",
    desc: "Theatrical warmth, softer drama.",
    isNew: false,
    hero: { background: "linear-gradient(160deg,#F8E2E8,#ECBECD)" },
    origin: "arts",
    originLabel: "🎨 Arts",
    unlockType: "achievement",
    achievement: { achievementType: "monthsActive", value: 3 },
    preview: {
      background: "linear-gradient(160deg,#FBEAEF,#F0C8D4)",
    },
    variables: baseVars({
      "--pt-bg": "#FBF0F3",
      "--pt-bg-image": "linear-gradient(160deg, #FBEAEF 0%, #F0C8D4 100%)",
      "--pt-accent": "#C47890",
      "--pt-accent-soft": "#FBEAEF",
      "--pt-btn-bg": "#C47890",
      "--pt-btn-border": "#C47890",
      "--pt-text": "#2A1820",
      "--pt-avatar-border": "#C47890",
    }),
  },
  lavenderEcho: {
    id: "lavenderEcho",
    audience: "creator",
    name: "Lavender Echo",
    desc: "Dreamy and airy, quieter and acoustic.",
    isNew: false,
    hero: { background: "linear-gradient(160deg,#EEE8FA,#D3C2F0)" },
    origin: "arts",
    originLabel: "🎨 Arts",
    unlockType: "achievement",
    achievement: { achievementType: "monthsActive", value: 3 },
    preview: {
      background: "linear-gradient(160deg,#EEE8F8,#DDD4F0)",
    },
    variables: baseVars({
      "--pt-bg": "#F4F0FA",
      "--pt-bg-image": "linear-gradient(160deg, #EEE8F8 0%, #DDD4F0 100%)",
      "--pt-accent": "#7A6AAA",
      "--pt-accent-soft": "#EEE8F8",
      "--pt-btn-bg": "#7A6AAA",
      "--pt-btn-border": "#7A6AAA",
      "--pt-text": "#1E1830",
      "--pt-avatar-border": "#7A6AAA",
    }),
  },

  /* ── Workshop ── */
  steelRust: {
    id: "steelRust",
    audience: "creator",
    name: "Steel & Rust",
    desc: "Brushed metal, a rust-orange stripe.",
    isNew: false,
    hero: { background: "linear-gradient(160deg,#E8E4DC,#CFC9BC)" },
    origin: "workshop",
    originLabel: "⚙ Workshop",
    unlockType: "credits",
    creditsCost: 2000,
    preview: {
      background: "#EDEBE7",
      backgroundImage:
        "repeating-linear-gradient(115deg,rgba(14,12,10,0.05) 0px,rgba(14,12,10,0.05) 2px,transparent 2px,transparent 7px)",
      borderTop: "4px solid #C4530A",
    },
    variables: baseVars({
      "--pt-bg": "#EDEBE7",
      "--pt-bg-image":
        "repeating-linear-gradient(115deg, rgba(14,12,10,0.05) 0px, rgba(14,12,10,0.05) 2px, transparent 2px, transparent 7px)",
      "--pt-surface": "#F7F6F3",
      "--pt-border": "#D0CCC4",
      "--pt-text": "#1A1714",
      "--pt-text-mid": "#4A453E",
      "--pt-text-muted": "#7A7268",
      "--pt-accent": "#C4530A",
      "--pt-accent-soft": "#F8EDE4",
      "--pt-btn-bg": "#C4530A",
      "--pt-btn-color": "#FFFFFF",
      "--pt-btn-border": "#C4530A",
      "--pt-btn-radius": "6px",
      "--pt-card-bg": "#F7F6F3",
      "--pt-card-border": "#D0CCC4",
      "--pt-card-radius": "6px",
      "--pt-media-border": "#C4530A",
      "--pt-media-border-width": "3px",
      "--pt-avatar-border": "#C4530A",
      "--pt-avatar-border-width": "3px",
      "--pt-name-font": '"Syne", system-ui, sans-serif',
      "--pt-name-weight": "800",
      "--pt-name-tracking": "-0.01em",
      "--pt-tier-top": "#C4530A",
      "--pt-tier-top-width": "4px",
    }),
  },
  fieldCanvas: {
    id: "fieldCanvas",
    audience: "creator",
    name: "Field Canvas",
    desc: "Woven canvas, forest green.",
    isNew: false,
    hero: { background: "#E8E0C8" },
    origin: "workshop",
    originLabel: "⚙ Workshop",
    unlockType: "achievement",
    achievement: { achievementType: "subscriberCount", value: 500 },
    preview: {
      background: "#EDE7D8",
      backgroundImage:
        "repeating-linear-gradient(0deg,rgba(14,12,10,0.035) 0px,rgba(14,12,10,0.035) 1px,transparent 1px,transparent 4px),repeating-linear-gradient(90deg,rgba(14,12,10,0.035) 0px,rgba(14,12,10,0.035) 1px,transparent 1px,transparent 4px)",
      borderTop: "4px solid #4A6741",
    },
    variables: baseVars({
      "--pt-bg": "#EDE7D8",
      "--pt-bg-image":
        "repeating-linear-gradient(0deg, rgba(14,12,10,0.035) 0px, rgba(14,12,10,0.035) 1px, transparent 1px, transparent 4px), repeating-linear-gradient(90deg, rgba(14,12,10,0.035) 0px, rgba(14,12,10,0.035) 1px, transparent 1px, transparent 4px)",
      "--pt-surface": "#F5F1E4",
      "--pt-border": "#D8CFB0",
      "--pt-text": "#2A2818",
      "--pt-text-mid": "#5A5540",
      "--pt-text-muted": "#8A8268",
      "--pt-accent": "#4A6741",
      "--pt-accent-soft": "#E8F0E4",
      "--pt-btn-bg": "#4A6741",
      "--pt-btn-color": "#FFFFFF",
      "--pt-btn-border": "#4A6741",
      "--pt-btn-radius": "6px",
      "--pt-card-bg": "#F5F1E4",
      "--pt-card-border": "#D8CFB0",
      "--pt-card-radius": "6px",
      "--pt-media-border": "#4A6741",
      "--pt-media-border-width": "3px",
      "--pt-avatar-border": "#4A6741",
      "--pt-avatar-border-width": "3px",
      "--pt-tier-top": "#4A6741",
      "--pt-tier-top-width": "4px",
    }),
  },
};

const EXTRA_THEME_DEFS = [
  /* Editorial */
  { id: "bone", name: "Bone", origin: "editorial", audience: "creator", unlockType: "credits", creditsCost: 1200, desc: "The quietest possible statement.", swatch: "#F3EDDF" },
  { id: "fineStripe", name: "Fine Stripe", origin: "editorial", audience: "creator", unlockType: "credits", creditsCost: 1600, desc: "A whisper of a pinstripe.", swatch: "repeating-linear-gradient(90deg,#F5F2EA,#F5F2EA 10px,#EDE7D8 10px,#EDE7D8 11px)" },
  { id: "herringbone", name: "Herringbone", origin: "editorial", audience: "creator", unlockType: "achievement", achievement: { achievementType: "monthsActive", value: 3 }, desc: "A woven fabric pattern, tailored.", swatch: "repeating-linear-gradient(45deg,#F2EEE2,#F2EEE2 6px,#E8E1CE 6px,#E8E1CE 8px)" },
  /* Workshop */
  { id: "torque", name: "Torque", origin: "workshop", audience: "creator", unlockType: "credits", creditsCost: 2000, desc: "Bold orange on warm concrete. Built to work in.", swatch: "linear-gradient(160deg,#EFE6D8,#D68A4A)" },
  { id: "toolbox", name: "Toolbox", origin: "workshop", audience: "creator", unlockType: "achievement", achievement: { achievementType: "subscriberCount", value: 500 }, desc: "Olive drab, practical, nothing wasted.", swatch: "#C9CBB0" },
  /* Wellness */
  { id: "morningFog", name: "Morning Fog", origin: "wellness", audience: "creator", unlockType: "credits", creditsCost: 1800, desc: "Pale mist over grass — the first hour of the day.", swatch: "linear-gradient(160deg,#EDF1EC,#D6E2D8)" },
  { id: "quietBloom", name: "Quiet Bloom", origin: "wellness", audience: "creator", unlockType: "achievement", achievement: { achievementType: "monthsActive", value: 3 }, desc: "Soft floral, unhurried, low light.", swatch: "linear-gradient(160deg,#F5EAE8,#E4D4DC)" },
  { id: "moss", name: "Moss", origin: "wellness", audience: "creator", unlockType: "achievement", achievement: { achievementType: "subscriberCount", value: 250 }, desc: "Muted sage, a calm green room.", swatch: "#A8B896" },
  { id: "dune", name: "Dune", origin: "wellness", audience: "creator", unlockType: "credits", creditsCost: 1200, desc: "Warm sand, somewhere quiet.", swatch: "#E8D5B0" },
  /* Beauty */
  { id: "rosewater", name: "Rosewater", origin: "beauty", audience: "creator", unlockType: "free", desc: "Soft pink and clean light — a fresh face, no filter.", swatch: "#F8E4E8" },
  { id: "vanity", name: "Vanity", origin: "beauty", audience: "creator", unlockType: "credits", creditsCost: 1800, desc: "Warm mirror-light, the kind that flatters everything.", swatch: "linear-gradient(160deg,#FBEEE4,#F0D4C4)" },
  { id: "bareSkin", name: "Bare Skin", origin: "beauty", audience: "creator", unlockType: "credits", creditsCost: 1600, desc: "Nude and honest, nothing to hide behind.", swatch: "#F5E4D8" },
  { id: "petal", name: "Petal", origin: "beauty", audience: "creator", unlockType: "achievement", achievement: { achievementType: "monthsActive", value: 3 }, desc: "Delicate pink, soft edges, quietly confident.", swatch: "linear-gradient(160deg,#FCE8EE,#F4C6D6)" },
  { id: "ivory", name: "Ivory", origin: "beauty", audience: "creator", unlockType: "credits", creditsCost: 1000, desc: "Pale and clean — the closest thing to a blank page.", swatch: "#FBF8F0" },
  { id: "houndstooth", name: "Houndstooth", origin: "beauty", audience: "creator", unlockType: "achievement", achievement: { achievementType: "subscriberCount", value: 500 }, desc: "A classic check, softened and warmed up.", swatch: "repeating-linear-gradient(45deg,#EDE6D4,#EDE6D4 5px,#DDD2B4 5px,#DDD2B4 10px)" },
  /* Food */
  { id: "honeycomb", name: "Honeycomb", origin: "food", audience: "creator", unlockType: "free", desc: "Warm amber gold, like light through a jar of honey.", swatch: "linear-gradient(160deg,#FCEFCB,#F5D57E)" },
  { id: "harvest", name: "Harvest", origin: "food", audience: "creator", unlockType: "credits", creditsCost: 1800, desc: "Wheat and squash, an autumn market table.", swatch: "linear-gradient(160deg,#F5E2C4,#DFA860)" },
  { id: "basil", name: "Basil", origin: "food", audience: "creator", unlockType: "credits", creditsCost: 1800, desc: "Fresh green, straight from the garden.", swatch: "#C3D3A8" },
  { id: "freshMarket", name: "Fresh Market", origin: "food", audience: "creator", unlockType: "achievement", achievement: { achievementType: "subscriberCount", value: 500 }, desc: "Cream and produce-crate wood, an early morning stall.", swatch: "#F0EAD8" },
  { id: "clay", name: "Clay", origin: "food", audience: "creator", unlockType: "credits", creditsCost: 1200, desc: "A grounded terracotta, nothing to prove.", swatch: "#D9A47A" },
  { id: "olive", name: "Olive", origin: "food", audience: "creator", unlockType: "credits", creditsCost: 1200, desc: "Muted and a little dusty, like an old field jacket.", swatch: "#8C9560" },
  /* Fitness */
  { id: "charge", name: "Charge", origin: "fitness", audience: "creator", unlockType: "free", desc: "Bright and warm — the feeling right before a set.", swatch: "linear-gradient(160deg,#FDE4D0,#F4A868)" },
  { id: "pulse", name: "Pulse", origin: "fitness", audience: "creator", unlockType: "credits", creditsCost: 2000, desc: "Coral-red, a little loud, built for momentum.", swatch: "linear-gradient(160deg,#FCE0DC,#EE8A78)" },
  { id: "iron", name: "Iron", origin: "fitness", audience: "creator", unlockType: "credits", creditsCost: 1800, desc: "Warm steel gray. Nothing soft about it.", swatch: "#D6D2C8" },
  { id: "momentum", name: "Momentum", origin: "fitness", audience: "creator", unlockType: "achievement", achievement: { achievementType: "subscriberCount", value: 500 }, desc: "Bold yellow-gold, always moving forward.", swatch: "linear-gradient(160deg,#FCEBC0,#F0C468)" },
  /* Auto & Trades */
  { id: "chrome", name: "Chrome", origin: "auto", audience: "creator", unlockType: "free", desc: "Polished steel-gray, catches the light just right.", swatch: "#DEDCD6" },
  { id: "racingStripe", name: "Racing Stripe", origin: "auto", audience: "creator", unlockType: "credits", creditsCost: 2000, desc: "A bold single stripe down a clean lane.", swatch: "repeating-linear-gradient(90deg,#F2F0EA,#F2F0EA 28px,#C4530A 28px,#C4530A 34px)" },
  { id: "garageFloor", name: "Garage Floor", origin: "auto", audience: "creator", unlockType: "credits", creditsCost: 1800, desc: "Sealed concrete gray, swept clean.", swatch: "#D8D5CC" },
  { id: "torqueDeep", name: "Torque Deep", origin: "auto", audience: "creator", unlockType: "achievement", achievement: { achievementType: "subscriberCount", value: 500 }, desc: "Rust-orange going deep — full throttle.", swatch: "linear-gradient(160deg,#E8E2D6,#C4530A)" },
  { id: "rust", name: "Rust", origin: "auto", audience: "creator", unlockType: "achievement", achievement: { achievementType: "subscriberCount", value: 250 }, desc: "Deeper, warmer — a color with some weight to it.", swatch: "#B5602E" },
  { id: "crossHatch", name: "Cross Hatch", origin: "auto", audience: "creator", unlockType: "credits", creditsCost: 1800, desc: "Two fine diagonals crossing — technical, precise.", swatch: "repeating-linear-gradient(45deg,transparent,transparent 9px,rgba(20,17,12,0.05) 9px,rgba(20,17,12,0.05) 10px),repeating-linear-gradient(-45deg,transparent,transparent 9px,rgba(20,17,12,0.05) 9px,rgba(20,17,12,0.05) 10px)", bg: "#F6F3E9" },
  /* Mind & Growth */
  { id: "parchment", name: "Parchment", origin: "mind", audience: "creator", unlockType: "free", desc: "Aged paper, the color of a well-read book.", swatch: "#F2ECD8" },
  { id: "deepFocus", name: "Deep Focus", origin: "mind", audience: "creator", unlockType: "credits", creditsCost: 1800, desc: "Muted purple-gray, made for quiet concentration.", swatch: "#B8AECC" },
  { id: "clarity", name: "Clarity", origin: "mind", audience: "creator", unlockType: "credits", creditsCost: 1600, desc: "Barely-there neutral. A clean, uncluttered mind.", swatch: "#EDEEE8" },
  { id: "growthRing", name: "Growth Ring", origin: "mind", audience: "creator", unlockType: "achievement", achievement: { achievementType: "monthsActive", value: 3 }, desc: "Warm tree-ring brown, patient and steady.", swatch: "linear-gradient(160deg,#EDE4D0,#C7A876)" },
  { id: "stone", name: "Stone", origin: "mind", audience: "creator", unlockType: "credits", creditsCost: 1000, desc: "Warm light gray. Neutral, but never cold.", swatch: "#E4DFD4" },
  /* Independent */
  { id: "blankCanvas", name: "Blank Canvas", origin: "independent", audience: "creator", unlockType: "free", desc: "As close to nothing as a color gets. Yours to fill.", swatch: "#FBFAF6" },
  { id: "wildcard", name: "Wildcard", origin: "independent", audience: "creator", unlockType: "credits", creditsCost: 2200, desc: "A little of everything, because you don't fit one box.", swatch: "linear-gradient(135deg,#F5D9A8,#E8A8C4,#B0D4C8)" },
  { id: "yourOwn", name: "Your Own", origin: "independent", audience: "creator", unlockType: "credits", creditsCost: 1600, desc: "Warm and neutral. A starting point, not a label.", swatch: "#E8DFC8" },
  /* For You (fan) */
  { id: "blush", name: "Blush", origin: "forYou", audience: "fan", unlockType: "credits", creditsCost: 600, desc: "Soft pink for your corner of the platform.", swatch: "#F5D9DE" },
  { id: "peach", name: "Peach", origin: "forYou", audience: "fan", unlockType: "credits", creditsCost: 600, desc: "Warm and a little sweet.", swatch: "#F8DCC4" },
  { id: "lilac", name: "Lilac", origin: "forYou", audience: "fan", unlockType: "achievement", achievement: { achievementType: "dropsUnlocked", value: 10 }, desc: "A soft purple for fans who've been here a while.", swatch: "#E4D9F0" },
  { id: "butter", name: "Butter", origin: "forYou", audience: "fan", unlockType: "credits", creditsCost: 600, desc: "Pale and sunny, no reason needed.", swatch: "#FAF0C8" },
  { id: "mint", name: "Mint", origin: "forYou", audience: "fan", unlockType: "credits", creditsCost: 600, desc: "Fresh and light, like the start of something.", swatch: "#DCEFE2" },
  { id: "coral", name: "Coral", origin: "forYou", audience: "fan", unlockType: "achievement", achievement: { achievementType: "dropsUnlocked", value: 10 }, desc: "Warm and cheerful, a little bit bold.", swatch: "#F6C9BC" },
  { id: "cloud", name: "Cloud", origin: "forYou", audience: "fan", unlockType: "credits", creditsCost: 500, desc: "Barely-there gray, soft as a Sunday morning.", swatch: "#F5F2ED" },
  { id: "sunset", name: "Sunset", origin: "forYou", audience: "fan", unlockType: "achievement", achievement: { achievementType: "dropsUnlocked", value: 25 }, desc: "A gentle fade from cream to coral.", swatch: "linear-gradient(160deg,#FADCC0,#F2A98A)" },
  { id: "polka", name: "Polka", origin: "forYou", audience: "fan", unlockType: "credits", creditsCost: 800, desc: "Soft polka dots, a little fun.", swatch: "radial-gradient(#E8B8C8 2.5px, #FBEEF2 2.5px)", swatchSize: "16px 16px" },
  { id: "confetti", name: "Confetti", origin: "forYou", audience: "fan", unlockType: "achievement", achievement: { achievementType: "dropsUnlocked", value: 25 }, desc: "A scatter of celebration.", swatch: "radial-gradient(#F2C87A 2px, transparent 2px), radial-gradient(#B8D4C0 1.5px, transparent 1.5px), radial-gradient(#E8AEC0 1.5px, transparent 1.5px)", swatchSize: "22px 22px, 14px 14px, 18px 18px", swatchPos: "0 0, 7px 5px, 3px 11px", bg: "#FDF9F0" },
  { id: "zigzag", name: "Zigzag", origin: "forYou", audience: "fan", unlockType: "credits", creditsCost: 900, desc: "A cheerful little chevron, nothing serious.", swatch: "linear-gradient(135deg,#F6E4C8 25%,transparent 25%),linear-gradient(225deg,#F6E4C8 25%,transparent 25%),linear-gradient(315deg,#F6E4C8 25%,transparent 25%),linear-gradient(45deg,#F6E4C8 25%,transparent 25%)", swatchSize: "16px 16px", swatchPos: "0 0, 0 0, 8px -8px, 8px -8px", bg: "#FDF7EA" },
  { id: "starlight", name: "Starlight", origin: "forYou", audience: "fan", unlockType: "achievement", achievement: { achievementType: "dropsUnlocked", value: 10 }, desc: "A sparse little sparkle across a soft lilac sky.", swatch: "radial-gradient(#D8C6EE 1.5px, transparent 1.5px)", swatchSize: "20px 20px", bg: "#F8F4FC" },
];

export const PROFILE_THEMES = {
  ...CORE_PROFILE_THEMES,
  ...Object.fromEntries(EXTRA_THEME_DEFS.map((d) => [d.id, extraThemeFromDef(d)])),
};

export const PROFILE_THEME_LIST = Object.values(PROFILE_THEMES);

export function resolveProfileThemeId(rawId) {
  if (!rawId) return DEFAULT_PROFILE_THEME_ID;
  const migrated = PROFILE_THEME_MIGRATIONS[rawId] || rawId;
  return PROFILE_THEMES[migrated] ? migrated : DEFAULT_PROFILE_THEME_ID;
}

export function getProfileTheme(themeId) {
  const id = resolveProfileThemeId(themeId);
  return PROFILE_THEMES[id];
}

/** Inline style object for a themed creator surface */
export function getProfileThemeStyle(themeId) {
  const theme = getProfileTheme(themeId);
  const vars = { ...theme.variables };
  const style = { ...vars };
  if (vars["--pt-bg-image"] && vars["--pt-bg-image"] !== "none") {
    style.backgroundImage = vars["--pt-bg-image"];
    style.backgroundColor = vars["--pt-bg"];
    if (vars["--pt-bg-size"]) style.backgroundSize = vars["--pt-bg-size"];
    if (vars["--pt-bg-position"]) style.backgroundPosition = vars["--pt-bg-position"];
  } else {
    style.background = vars["--pt-bg"];
  }
  return style;
}

export function formatThemeUnlockLabel(theme) {
  if (!theme) return "";
  if (theme.unlockType === "free") return "Free default";
  if (theme.unlockType === "credits") {
    return `${Number(theme.creditsCost || 0).toLocaleString("en-US")} credits`;
  }
  if (theme.unlockType === "achievement") {
    const a = theme.achievement || {};
    if (a.achievementType === "subscriberCount") {
      return `${Number(a.value || 0).toLocaleString("en-US")} subscribers`;
    }
    if (a.achievementType === "monthsActive") {
      return `${a.value} month${a.value === 1 ? "" : "s"} active`;
    }
    if (a.achievementType === "dropsUnlocked") {
      return `${a.value} drops unlocked`;
    }
  }
  return "Locked";
}

export function isThemeOwned(theme, {
  unlockedIds = [],
  subscriberCount = 0,
  monthsActive = 0,
  dropsUnlocked = 0,
} = {}) {
  if (!theme) return false;
  if (theme.unlockType === "free") return true;
  if (unlockedIds.includes(theme.id)) return true;
  if (theme.unlockType === "achievement") {
    const a = theme.achievement || {};
    if (a.achievementType === "subscriberCount") {
      return subscriberCount >= (a.value || 0);
    }
    if (a.achievementType === "monthsActive") {
      return monthsActive >= (a.value || 0);
    }
    if (a.achievementType === "dropsUnlocked") {
      return dropsUnlocked >= (a.value || 0);
    }
  }
  return false;
}

export function monthsActiveFromCreatedAt(createdAt) {
  if (!createdAt) return 0;
  const d =
    typeof createdAt?.toDate === "function"
      ? createdAt.toDate()
      : createdAt?.seconds
        ? new Date(createdAt.seconds * 1000)
        : createdAt instanceof Date
          ? createdAt
          : new Date(createdAt);
  if (Number.isNaN(d.getTime())) return 0;
  const now = new Date();
  const months =
    (now.getFullYear() - d.getFullYear()) * 12 + (now.getMonth() - d.getMonth());
  return Math.max(0, months);
}

/** Default missing audience to creator so untagged entries stay creator-only. */
export function themeMatchesAudience(theme, userProfile) {
  const audience = theme?.audience || "creator";
  if (audience === "both") return true;
  const creator = hasCreatorAccess(userProfile);
  if (audience === "creator") return creator;
  if (audience === "fan") return !creator;
  return false;
}

export function catalogThemesForUser(userProfile) {
  return PROFILE_THEME_LIST.filter((t) => themeMatchesAudience(t, userProfile));
}

export function originDisplayName(origin) {
  return PROFILE_THEME_ORIGIN_LABELS[origin] || origin || "";
}

export function audienceDisplayLabel(audience) {
  if (audience === "fan") return "Fans";
  if (audience === "both") return "Everyone";
  return "Creators";
}

export function dropsUnlockedFromUser(user) {
  const map = user?.unlockedDrops;
  if (!map || typeof map !== "object") return 0;
  return Object.keys(map).length;
}

/**
 * Theme id to apply on a profile page. Returns null when the stored value is
 * missing or not allowed for that profile owner (no creator-theme leak onto fans).
 */
export function displayProfileThemeId(rawId, profileOwner) {
  if (!rawId) return null;
  const migrated = PROFILE_THEME_MIGRATIONS[rawId] || rawId;
  const theme = PROFILE_THEMES[migrated];
  if (!theme || !themeMatchesAudience(theme, profileOwner)) return null;
  return migrated;
}
